import { useState } from "react";
import { ChevronDown, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { useGetFinancialOverviewQuery } from "@/redux/services/expense/expense.api";
import { cn, displayPrice } from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { getExpenseCategoryLabel } from "@/lib/expenses";
import { reportCard, reportInnerCard } from "@/components/pos/reports/reportCard";
import LabelHint from "@/components/ui/LabelHint";

interface FinancialOverviewCardProps {
  // Controlled by the Rapports page date-range picker, so the overview always
  // reflects the same period as the sales report it sits next to.
  startDate: string; // yyyy-MM-dd
  endDate: string; // yyyy-MM-dd
}

// Semantic accents drawn from the Rapports palette: revenue teal (matches
// "Total encaissé"), expenses red, net follows its sign.
const REVENUE_ACCENT = "#066a69";
const EXPENSE_ACCENT = "#dc2626";
const POSITIVE_ACCENT = "#16a34a";

// Collapsed by default; remember the manager's preference across visits once
// they expand it.
const STORAGE_KEY = "reports.financialOverview.open";

const FinancialOverviewCard = ({
  startDate,
  endDate,
}: FinancialOverviewCardProps) => {
  const currency = useRestaurantCurrency();

  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  });

  const toggle = () =>
    setOpen((prev) => {
      const next = !prev;
      window.localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });

  const { data, isFetching } = useGetFinancialOverviewQuery({
    startDate,
    endDate,
  });

  const current = data?.data.current;
  const net = current?.net ?? 0;
  const netAccent = net < 0 ? EXPENSE_ACCENT : POSITIVE_ACCENT;

  return (
    <section className={cn(reportCard, "overflow-hidden")}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 p-5 text-left transition-colors hover:bg-muted/40"
      >
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Aperçu financier
          </h2>
          <p className="mt-0.5 text-xs normal-case text-muted-foreground">
            Résumé revenus / dépenses — pas un compte de résultat officiel.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {/* Net stays visible even when collapsed — the one figure worth a
              glance without expanding. */}
          <span className="hidden text-right sm:block">
            <span className="block text-[11px] uppercase tracking-wide text-muted-foreground">
              Net
            </span>
            <span
              className={cn(
                "text-sm font-semibold tabular-nums",
                isFetching && "animate-pulse opacity-50",
              )}
              style={{ color: netAccent }}
            >
              {displayPrice(net, currency)}
            </span>
          </span>
          <ChevronDown
            className={cn(
              "h-5 w-5 text-muted-foreground transition-transform",
              open && "rotate-180",
            )}
          />
        </div>
      </button>

      {open && (
        <div className="space-y-4 border-t border-border/60 p-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <StatTile
              label="Revenus"
              labelHint="Tout l'argent encaissé sur la période, y compris les acomptes et les paiements sur commandes annulées ou refusées (vue de rapprochement caisse). Diffère du « Total encaissé » du rapport de ventes, qui ne compte que les commandes valides."
              value={displayPrice(current?.revenue.total ?? 0, currency)}
              icon={TrendingUp}
              accent={REVENUE_ACCENT}
              loading={isFetching}
            />
            <StatTile
              label="Dépenses"
              value={displayPrice(current?.expenses.total ?? 0, currency)}
              icon={TrendingDown}
              accent={EXPENSE_ACCENT}
              loading={isFetching}
            />
            <StatTile
              label="Net"
              value={displayPrice(net, currency)}
              icon={Wallet}
              accent={netAccent}
              valueColor={netAccent}
              loading={isFetching}
            />
          </div>

          {current && current.expenses.byCategory.length > 0 && (
            <div className="border-t border-border/60 pt-4">
              <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Dépenses par catégorie
              </h3>
              <ul className="space-y-1">
                {current.expenses.byCategory.map((row) => (
                  <li
                    key={row.uuid}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-card-foreground">
                      {getExpenseCategoryLabel(row)}
                    </span>
                    <span className="font-medium tabular-nums text-card-foreground">
                      {displayPrice(row.total, currency)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const StatTile = ({
  label,
  labelHint,
  value,
  icon: Icon,
  accent,
  valueColor,
  loading,
}: {
  label: string;
  labelHint?: string;
  value: string;
  icon: React.ElementType;
  accent: string;
  valueColor?: string;
  loading: boolean;
}) => (
  <div className={cn(reportInnerCard, "p-4")}>
    <div className="mb-2 flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
        {labelHint && <LabelHint text={labelHint} />}
      </span>
      <div
        className="flex h-8 w-8 items-center justify-center rounded-md"
        style={{ backgroundColor: `${accent}1a` }}
      >
        <Icon className="h-4 w-4" style={{ color: accent }} />
      </div>
    </div>
    <p
      className={cn(
        "text-xl font-semibold tabular-nums",
        loading && "animate-pulse opacity-50",
      )}
      style={{ color: valueColor ?? "inherit" }}
    >
      {value}
    </p>
  </div>
);

export default FinancialOverviewCard;
